import { useEffect, useState } from 'react';

import { measureLabelExtent, type MeasureLabelOptions } from './measure-label';

/** 라벨 dependency key 분리자 — 라벨(콤마·공백)과 무충돌하는 제어문자(unit separator U+001F). */
const LABEL_SEP = '';

/**
 * 값축(세로 막대 Y) tick 라벨 실측 옵션 — 폰트는 축 tickStyle(11px), pad = tick 텍스트가 축선에서 떨어지는
 * 오프셋 8(recharts tickSize 6 + tickMargin 2) + 렌더 오차 여유 4. 단위 라벨은 폭 산정에서 제외한다(축 폭이
 * 아니라 plot 상단 여백 밴드를 쓰도록 `ValueAxisUnitLabel` 이 축 바깥 모서리 정렬). `max` 는 이상치 폭주 방지.
 */
const VALUE_AXIS_MEASURE: MeasureLabelOptions = { fontPx: 11, pad: 12, min: 24, max: 72 };

/**
 * 가로 막대 **카테고리(행 라벨) 폭** 실측 hook — 긴 한글 라벨(예: '강원특별자치도')이 recharts `width="auto"` 로
 * 잘리는 것을 막는다. 스크롤(`ScrollFrame`)·비스크롤(direct) 두 경로가 공유해 카테고리 폭 산출을 단일화한다.
 */
export function useCategoryWidth(xLabels: string[]): number {
  return useMeasuredWidth(xLabels);
}

/**
 * **값축 폭** 실측 hook — 고정 44px 대신 실제 tick 라벨(포맷 적용 후) 폭에 맞춘다. recharts 의 `width="auto"` 는
 * `label`(단위) 이 React element 면 계산 자체를 건너뛰므로(YAxis.js `isValidElement(label)` 가드) 쓸 수 없어
 * 직접 실측한다. 좁은 화면에서 좌·우 축이 각각 44px 씩 먹던 여백을 라벨 실폭까지 줄이는 것이 목적.
 */
export function useValueAxisWidth(tickLabels: string[]): number {
  return useMeasuredWidth(tickLabels, VALUE_AXIS_MEASURE);
}

/**
 * 라벨 목록 → 실측 폭. 초기값은 즉시 실측하고, 폰트 로드(FOUT) 후 `document.fonts.ready` 에서 재측정(폴백 폰트
 * 메트릭 보정). `options` 는 모듈 상수(안정 참조) 전제.
 */
function useMeasuredWidth(labels: string[], options?: MeasureLabelOptions): number {
  const labelKey = labels.join(LABEL_SEP);
  const [width, setWidth] = useState(() => measureLabelExtent(labels, options));
  useEffect(() => {
    let alive = true;
    const measure = () => {
      if (alive) setWidth(measureLabelExtent(labelKey ? labelKey.split(LABEL_SEP) : [], options));
    };
    measure();
    if (typeof document !== 'undefined') void document.fonts?.ready.then(measure).catch(() => {});
    return () => {
      alive = false;
    };
  }, [labelKey, options]);
  return width;
}
